import {
    differenceInDays,
    endOfWeek,
    format,
    min,
    startOfMonth,
    startOfWeek,
    subDays,
    subMonths,
} from 'date-fns';
import { darken, getLuminance, lighten } from 'polished';

/**
 * Utilitários do dashboard de relatórios de limpeza (normalização da API, rótulos, séries).
 */

function pad2(n) {
    return String(n).padStart(2, '0');
}

/**
 * Instante em ISO 8601 com offset do fuso local (ex.: 2026-03-17T00:00:00-03:00).
 */
function localDateToOffsetISO(date) {
    const y = date.getFullYear();
    const mo = pad2(date.getMonth() + 1);
    const d = pad2(date.getDate());
    const h = pad2(date.getHours());
    const mi = pad2(date.getMinutes());
    const s = pad2(date.getSeconds());
    const offsetMin = -date.getTimezoneOffset();
    const sign = offsetMin >= 0 ? '+' : '-';
    const abs = Math.abs(offsetMin);
    const oh = pad2(Math.floor(abs / 60));
    const om = pad2(abs % 60);
    return `${y}-${mo}-${d}T${h}:${mi}:${s}${sign}${oh}:${om}`;
}

/**
 * A partir de YYYY-MM-DD (input type="date"), monta o intervalo para a API:
 * início às 00:00:00 do primeiro dia e fim às 23:59:59 do último dia, no fuso local do navegador.
 */
export function buildCleaningReportApiTimeRange(startYmd, endYmd) {
    const parseYmd = ymd => {
        if (!ymd || typeof ymd !== 'string') return null;
        const p = ymd.split('-').map(x => parseInt(x, 10));
        if (p.length !== 3 || p.some(n => Number.isNaN(n))) return null;
        return { y: p[0], m: p[1], d: p[2] };
    };

    const startParts = parseYmd(startYmd);
    const endParts = parseYmd(endYmd);

    if (!startParts || !endParts) {
        return {
            start_time: `${startYmd}T00:00:00`,
            end_time: `${endYmd}T23:59:59`,
        };
    }

    const startDate = new Date(
        startParts.y,
        startParts.m - 1,
        startParts.d,
        0,
        0,
        0,
        0
    );
    const endDate = new Date(
        endParts.y,
        endParts.m - 1,
        endParts.d,
        23,
        59,
        59,
        0
    );

    return {
        start_time: localDateToOffsetISO(startDate),
        end_time: localDateToOffsetISO(endDate),
    };
}

const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = 3600;

/**
 * Formata duração total em segundos para leitura humana (s / min / h).
 */
export function formatCleaningDuration(seconds) {
    const s = Math.max(0, Math.floor(Number(seconds) || 0));
    if (s < SECONDS_PER_MINUTE) {
        return `${s} s`;
    }
    if (s < SECONDS_PER_HOUR) {
        const m = Math.floor(s / SECONDS_PER_MINUTE);
        const rs = s % SECONDS_PER_MINUTE;
        return rs ? `${m} min ${rs} s` : `${m} min`;
    }
    const h = Math.floor(s / SECONDS_PER_HOUR);
    const r = s % SECONDS_PER_HOUR;
    const m = Math.floor(r / SECONDS_PER_MINUTE);
    const rs = r % SECONDS_PER_MINUTE;
    const parts = [`${h} h`];
    if (m) parts.push(`${m} min`);
    if (rs) parts.push(`${rs} s`);
    return parts.join(' ');
}

/**
 * Formata duração total sem exibir os segundos (usado nos cards de resumo).
 */
export function formatCleaningDurationNoSeconds(seconds) {
    const s = Math.max(0, Math.floor(Number(seconds) || 0));
    if (s < SECONDS_PER_MINUTE) {
        return '< 1 min';
    }
    if (s < SECONDS_PER_HOUR) {
        const m = Math.floor(s / SECONDS_PER_MINUTE);
        return `${m} min`;
    }
    const h = Math.floor(s / SECONDS_PER_HOUR);
    const r = s % SECONDS_PER_HOUR;
    const m = Math.floor(r / SECONDS_PER_MINUTE);
    const parts = [`${h} h`];
    if (m) parts.push(`${m} min`);
    return parts.join(' ');
}

/**
 * Momento de início da tarefa (Pudu: `start_time` em Unix). Não usar `create_time` (sync/registro).
 */
export function getCleaningReportTaskStartTime(row) {
    if (!row || typeof row !== 'object') return undefined;
    if (row.start_time !== undefined && row.start_time !== null) {
        return row.start_time;
    }
    return undefined;
}

/**
 * Chave de dia YYYY-MM-DD a partir de `start_time` (Unix) ou string de data/hora.
 */
export function getCleaningReportDayKey(createTime) {
    if (createTime == null || createTime === '') {
        return '—';
    }
    if (typeof createTime === 'number' && Number.isFinite(createTime)) {
        const ms = createTime > 1e12 ? createTime : createTime * 1000;
        const date = new Date(ms);
        if (Number.isNaN(date.getTime())) {
            return '—';
        }
        const y = date.getFullYear();
        const mo = pad2(date.getMonth() + 1);
        const d = pad2(date.getDate());
        return `${y}-${mo}-${d}`;
    }
    const s = String(createTime).trim();
    if (!s) {
        return '—';
    }

    let head;
    if (s.includes('T')) {
        head = s.split('T')[0].trim();
    } else {
        head = s.split(/\s+/)[0].trim();
    }

    if (/^\d{4}-\d{2}-\d{2}$/.test(head)) {
        return head;
    }

    const prefix = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
    if (prefix) {
        return `${prefix[1]}-${prefix[2]}-${prefix[3]}`;
    }

    return '—';
}

/**
 * Rótulo curto para eixo do gráfico a partir da chave YYYY-MM-DD (calendário estável).
 */
export function cleaningReportYmdToChartLabel(dayKey, localeFormat) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dayKey)) {
        return dayKey;
    }
    const [yStr, moStr, dStr] = dayKey.split('-');
    const localCal = new Date(
        Number(yStr),
        parseInt(moStr, 10) - 1,
        parseInt(dStr, 10)
    );
    if (Number.isNaN(localCal.getTime())) {
        return dayKey;
    }
    return localCal.toLocaleDateString(localeFormat, {
        month: '2-digit',
        day: '2-digit',
    });
}

/** Rótulo de mês para eixo (YYYY-MM). */
function cleaningReportYmToChartLabel(ymKey, localeFormat) {
    if (!/^\d{4}-\d{2}$/.test(ymKey)) {
        return ymKey;
    }
    const [yStr, moStr] = ymKey.split('-');
    const localCal = new Date(Number(yStr), parseInt(moStr, 10) - 1, 1);
    if (Number.isNaN(localCal.getTime())) {
        return ymKey;
    }
    return localCal.toLocaleDateString(localeFormat, {
        month: 'short',
        year: 'numeric',
    });
}

/**
 * Data de criação: dd/mm/aaaa a partir da parte data do create_time da API (sem adiantar/atrasar por fuso).
 */
export function formatCleaningReportCreateDate(createTime) {
    if (createTime == null || String(createTime).trim() === '') {
        return '—';
    }
    const key = getCleaningReportDayKey(createTime);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(key)) {
        return key === '—' ? '—' : String(createTime).trim();
    }
    const [y, m, d] = key.split('-');
    return `${d}/${m}/${y}`;
}
/**
 * Extrai horário literal de create_time (após a data), sem converter fuso.
 * Ex.: "2023-08-31 23:52:08" → "23:52:08"; ISO após T → mesma lógica.
 */
function extractCleaningReportTimePart(createTime) {
    if (createTime == null || typeof createTime === 'number') {
        return '';
    }
    const str = String(createTime).trim();
    if (!str) {
        return '';
    }

    if (str.includes('T')) {
        const afterT = str.split('T')[1] || '';
        const m = /^(\d{2}):(\d{2})(?::(\d{2}))?/.exec(afterT);
        if (m) {
            return m[3] !== undefined
                ? `${m[1]}:${m[2]}:${m[3]}`
                : `${m[1]}:${m[2]}`;
        }
        return '';
    }

    const spaceIdx = str.search(/\s/);
    if (spaceIdx < 0) {
        return '';
    }
    const rest = str.slice(spaceIdx + 1).trim();
    const m = /^(\d{2}):(\d{2})(?::(\d{2}))?/.exec(rest);
    if (m) {
        return m[3] !== undefined
            ? `${m[1]}:${m[2]}:${m[3]}`
            : `${m[1]}:${m[2]}`;
    }
    return '';
}

/**
 * Timestamp em ms para ordenar por data de criação.
 * Valores inválidos → -Infinity (ficam no fim quando a ordem é decrescente).
 */
function createTimeToSortMs(createTime) {
    if (createTime == null || createTime === '') {
        return Number.NEGATIVE_INFINITY;
    }
    if (typeof createTime === 'number' && Number.isFinite(createTime)) {
        return createTime > 1e12 ? createTime : createTime * 1000;
    }
    const s = String(createTime).trim();
    if (!s) {
        return Number.NEGATIVE_INFINITY;
    }
    const withT = /^\d{4}-\d{2}-\d{2}\s/.test(s) ? s.replace(/\s+/, 'T') : s;
    let ms = Date.parse(withT);
    if (!Number.isNaN(ms)) {
        return ms;
    }
    const day = getCleaningReportDayKey(createTime);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) {
        return Number.NEGATIVE_INFINITY;
    }
    const timePart = extractCleaningReportTimePart(createTime) || '00:00:00';
    ms = Date.parse(`${day}T${timePart}`);
    return Number.isNaN(ms) ? Number.NEGATIVE_INFINITY : ms;
}

/**
 * Lista ordenada por start_time decrescente (início da tarefa, mais recente primeiro).
 */
export function sortCleaningReportListByCreateTimeDesc(list) {
    if (!Array.isArray(list) || list.length === 0) {
        return Array.isArray(list) ? [...list] : [];
    }
    return [...list].sort(
        (a, b) =>
            createTimeToSortMs(getCleaningReportTaskStartTime(b)) -
            createTimeToSortMs(getCleaningReportTaskStartTime(a))
    );
}

/**
 * Desempacota `details` (persistido pelo backend a partir da API de detalhes da Pudu)
 * nas propriedades diretas da tarefa esperadas pela UI dos dashboards.
 */
export function normalizeCleaningReportTask(task) {
    if (!task || typeof task !== 'object') return task;
    const details =
        task.details && typeof task.details === 'object' ? task.details : null;
    if (!details) return task;

    const maps =
        (Array.isArray(task.maps) && task.maps.length > 0 && task.maps) ||
        (Array.isArray(details.maps) &&
            details.maps.length > 0 &&
            details.maps) ||
        (Array.isArray(details.floor_list) &&
            details.floor_list.length > 0 &&
            details.floor_list) ||
        (Array.isArray(task.floor_list) &&
            task.floor_list.length > 0 &&
            task.floor_list) ||
        [];

    return {
        ...details,
        ...task,
        cost_water:
            task.cost_water !== undefined
                ? task.cost_water
                : details.cost_water,
        cost_battery:
            task.cost_battery !== undefined
                ? task.cost_battery
                : details.cost_battery,
        power_consumption:
            task.power_consumption !== undefined
                ? task.power_consumption
                : details.power_consumption,
        maps,
        floor_list:
            task.floor_list !== undefined
                ? task.floor_list
                : details.floor_list,
        task_name:
            task.task_name !== undefined ? task.task_name : details.task_name,
        average_area:
            task.average_area !== undefined
                ? task.average_area
                : details.average_area,
        details,
    };
}

/**
 * Normaliza resposta do backend / Pudu para { total, offset, limit, list }.
 */
export function normalizeCleaningReportResponse(body) {
    if (!body || typeof body !== 'object') {
        return { total: 0, offset: 0, limit: 0, list: [] };
    }
    if (body.error && body.code) {
        return {
            total: 0,
            offset: 0,
            limit: 0,
            list: [],
            apiError: body,
        };
    }

    let inner = body;
    if (
        body.data != null &&
        typeof body.data === 'object' &&
        !Array.isArray(body.data)
    ) {
        inner = body.data;
    }

    let list = [];
    if (Array.isArray(inner.list)) {
        list = inner.list;
    } else if (Array.isArray(body.list)) {
        list = body.list;
    }

    let totalRaw;
    if (inner.total !== undefined) {
        totalRaw = inner.total;
    } else if (body.total !== undefined) {
        totalRaw = body.total;
    } else {
        totalRaw = list.length;
    }
    const total = Number(totalRaw) || 0;

    let offsetRaw;
    if (inner.offset !== undefined) {
        offsetRaw = inner.offset;
    } else if (body.offset !== undefined) {
        offsetRaw = body.offset;
    } else {
        offsetRaw = 0;
    }
    const offset = Number(offsetRaw) || 0;

    let limitRaw;
    if (inner.limit !== undefined) {
        limitRaw = inner.limit;
    } else if (body.limit !== undefined) {
        limitRaw = body.limit;
    } else {
        limitRaw = list.length;
    }
    const limit = Number(limitRaw) || 0;

    const normalizedList = sortCleaningReportListByCreateTimeDesc(list).map(
        normalizeCleaningReportTask
    );

    return {
        total,
        offset,
        limit,
        list: normalizedList,
        consumption: body.consumption || inner.consumption || null,
        detail_sync: body.detail_sync || inner.detail_sync || null,
    };
}

/**
 * Intervalo padrão para painel ao vivo (últimos 7 dias, fim = hoje).
 */
export function defaultLiveCleaningReportDateRange() {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 7);
    const ymd = d =>
        `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
    return {
        startDate: ymd(start),
        endDate: ymd(end),
    };
}

/**
 * Semana civil atual (segunda → hoje), fuso local. Para preview no dashboard.
 */
export function currentWeekCleaningReportDateRange(now = new Date()) {
    const start = startOfWeek(now, { weekStartsOn: 1 });
    const weekEnd = endOfWeek(now, { weekStartsOn: 1 });
    const end = min([weekEnd, now]);
    return {
        startDate: format(start, 'yyyy-MM-dd'),
        endDate: format(end, 'yyyy-MM-dd'),
    };
}

/**
 * Doze meses corridos (início do mês há 11 meses → hoje). Para gráfico por mês no dashboard.
 */
export function lastTwelveMonthsCleaningReportDateRange(now = new Date()) {
    const start = startOfMonth(subMonths(now, 11));
    return {
        startDate: format(start, 'yyyy-MM-dd'),
        endDate: format(now, 'yyyy-MM-dd'),
    };
}

/**
 * Mês civil atual (dia 1 → hoje), fuso local. Para preview diário no dashboard.
 */
export function currentMonthToDateCleaningReportDateRange(now = new Date()) {
    const start = startOfMonth(now);
    return {
        startDate: format(start, 'yyyy-MM-dd'),
        endDate: format(now, 'yyyy-MM-dd'),
    };
}

/**
 * Trinta dias corridos (hoje e os 29 anteriores), fuso local.
 */
export function last30DaysCleaningReportDateRange(now = new Date()) {
    return {
        startDate: format(subDays(now, 29), 'yyyy-MM-dd'),
        endDate: format(now, 'yyyy-MM-dd'),
    };
}

/** `code` em `robots_errors[]` (API / CleaningReportModule) → chave LocaleMessage. */
const CLEANING_REPORT_ROBOT_ERROR_MSG_BY_CODE = {
    invalid_settings:
        'page.robots.cleaning_report.robot_error.invalid_settings',
    unknown_error: 'page.robots.cleaning_report.robot_error.unknown_error',
    robot_not_found: 'page.robots.cleaning_report.robot_error.robot_not_found',
    invalid_query: 'page.robots.cleaning_report.robot_error.invalid_query',
};

/**
 * @param {string|null|undefined} code
 * @returns {string|null} chave de mensagem ou null se não houver tradução fixa para o código
 */
export function getCleaningReportRobotErrorMsgKey(code) {
    if (code == null || code === '') return null;
    const k = String(code).trim();
    return CLEANING_REPORT_ROBOT_ERROR_MSG_BY_CODE[k] || null;
}

/**
 * Resposta agregada GET /robots/cleaning-report/cc1 (lista multi-robô + metadados).
 */
export function normalizeAggregatedCleaningReportResponse(body) {
    if (!body || typeof body !== 'object') {
        return {
            list: [],
            total: 0,
            offset: 0,
            limit: 0,
            robots_queried: 0,
            robots_errors: [],
        };
    }
    if (body.error && body.code) {
        return {
            list: [],
            total: 0,
            offset: 0,
            limit: 0,
            robots_queried: 0,
            robots_errors: [],
            apiError: body,
        };
    }
    const list = Array.isArray(body.list) ? body.list : [];
    const sorted = sortCleaningReportListByCreateTimeDesc(list).map(
        normalizeCleaningReportTask
    );
    const total = Number(body.total) || sorted.length;
    return {
        list: sorted,
        total,
        offset: Number(body.offset) || 0,
        limit: Number(body.limit) || sorted.length,
        robots_queried: Number(body.robots_queried) || 0,
        robots_errors: Array.isArray(body.robots_errors)
            ? body.robots_errors
            : [],
        applications: body.applications || {},
        calculated_metrics: body.calculated_metrics || null,
        consumption: body.consumption || null,
        detail_sync: body.detail_sync || null,
    };
}

/**
 * Data e hora de criação: dd/mm/aaaa HH:mm:ss (valores literais da API quando em string).
 */
export function formatCleaningReportCreateDateTime(createTime) {
    if (createTime == null || String(createTime).trim() === '') {
        return '—';
    }
    if (typeof createTime === 'number' && Number.isFinite(createTime)) {
        const ms = createTime > 1e12 ? createTime : createTime * 1000;
        const date = new Date(ms);
        if (Number.isNaN(date.getTime())) {
            return '—';
        }
        const d = pad2(date.getDate());
        const mo = pad2(date.getMonth() + 1);
        const y = date.getFullYear();
        const h = pad2(date.getHours());
        const mi = pad2(date.getMinutes());
        const sec = pad2(date.getSeconds());
        return `${d}/${mo}/${y} ${h}:${mi}:${sec}`;
    }

    const dateStr = formatCleaningReportCreateDate(createTime);
    if (dateStr === '—' || !/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) {
        return dateStr;
    }

    const timePart = extractCleaningReportTimePart(createTime);
    return timePart ? `${dateStr} ${timePart}` : dateStr;
}

/**
 * Modo de limpeza (Pudu): 1 = lavagem molhada, 2 = lavagem a seco.
 * Validar com a doc “Cleaning Report - Details”: open.pudutech.com (cloud-api).
 */
export function getCleaningModeLabel(mode, localeCode = 'pt_BR') {
    const m = Number(mode);
    const en = localeCode === 'en_US';
    if (m === 1) return en ? 'Wet cleaning' : 'Lavagem molhada';
    if (m === 2) return en ? 'Dry cleaning' : 'Lavagem a seco';
    return mode != null ? String(mode) : '?';
}

/** Códigos de status de tarefa de limpeza (Pudu). */
const CLEANING_TASK_STATUS_CODES = [0, 1, 2, 3, 4, 5, 6];

/**
 * Status da tarefa de limpeza (Pudu): 0–6 conforme API.
 * Tabela oficial: documentação “Cleaning Report - Details” (Pudu Open Platform).
 */
export function getCleaningTaskStatusLabel(status, localeCode = 'pt_BR') {
    const s = Number(status);
    const en = localeCode === 'en_US';
    const labels = en
        ? {
              0: 'Not started / Default',
              1: 'Cleaning',
              2: 'Task paused',
              3: 'Task interrupted',
              4: 'Task ended',
              5: 'Task exception',
              6: 'Task canceled',
          }
        : {
              0: 'Não iniciada / Padrão',
              1: 'Em limpeza',
              2: 'Tarefa pausada',
              3: 'Tarefa interrompida',
              4: 'Tarefa concluída',
              5: 'Exceção na tarefa',
              6: 'Tarefa cancelada',
          };
    if (Object.prototype.hasOwnProperty.call(labels, s)) return labels[s];
    return status != null ? String(status) : '?';
}

/** Mapa status numérico → rótulo (pizza por status). */
export function getCleaningStatusLabelMap(localeCode = 'pt_BR') {
    return CLEANING_TASK_STATUS_CODES.reduce((acc, code) => {
        acc[code] = getCleaningTaskStatusLabel(code, localeCode);
        return acc;
    }, {});
}

/**
 * Série para gráfico de área: tempo total de limpeza por dia.
 * Escala o eixo Y em segundos, minutos ou horas conforme o maior total diário.
 * Cada ponto: { date, value (escala), secondsTotal (bruto) }.
 */
export function buildAreaSeriesFromList(list, localeFormat = 'pt-BR') {
    if (!list || !list.length) {
        return { data: [], unit: 'seconds' };
    }
    const byDay = {};
    list.forEach(item => {
        const day = getCleaningReportDayKey(
            getCleaningReportTaskStartTime(item)
        );
        if (day === '—') return;
        byDay[day] = (byDay[day] || 0) + (Number(item.clean_time) || 0);
    });
    const sortedDays = Object.keys(byDay).sort();
    const points = sortedDays.map(day => ({
        date: cleaningReportYmdToChartLabel(day, localeFormat),
        secondsTotal: byDay[day],
    }));
    const maxSec = Math.max(0, ...points.map(p => p.secondsTotal));
    let unit = 'seconds';
    let divisor = 1;
    if (maxSec >= SECONDS_PER_HOUR) {
        unit = 'hours';
        divisor = SECONDS_PER_HOUR;
    } else if (maxSec >= SECONDS_PER_MINUTE) {
        unit = 'minutes';
        divisor = SECONDS_PER_MINUTE;
    }
    const data = points.map(p => ({
        date: p.date,
        value: Math.round((p.secondsTotal / divisor) * 100) / 100,
        secondsTotal: p.secondsTotal,
    }));
    return { data, unit };
}

/**
 * Série para gráfico de área: tempo total de limpeza agregado por mês (YYYY-MM).
 * Sempre 12 pontos (doze meses corridos até `referenceDate`), com 0 nos meses sem dados.
 * Mesma escala de unidade que {@link buildAreaSeriesFromList}.
 */
export function buildAreaSeriesByMonthFromList(
    list,
    localeFormat = 'pt-BR',
    referenceDate = new Date()
) {
    const monthKeys = [];
    for (let i = 11; i >= 0; i -= 1) {
        const d = startOfMonth(subMonths(referenceDate, i));
        monthKeys.push(format(d, 'yyyy-MM'));
    }

    const byMonth = {};
    (list || []).forEach(item => {
        const day = getCleaningReportDayKey(
            getCleaningReportTaskStartTime(item)
        );
        if (day === '—' || !/^\d{4}-\d{2}-\d{2}$/.test(day)) return;
        const ym = day.slice(0, 7);
        byMonth[ym] = (byMonth[ym] || 0) + (Number(item.clean_time) || 0);
    });

    const points = monthKeys.map(ym => ({
        date: cleaningReportYmToChartLabel(ym, localeFormat),
        secondsTotal: byMonth[ym] || 0,
    }));
    const maxSec = Math.max(0, ...points.map(p => p.secondsTotal));
    let unit = 'seconds';
    let divisor = 1;
    if (maxSec >= SECONDS_PER_HOUR) {
        unit = 'hours';
        divisor = SECONDS_PER_HOUR;
    } else if (maxSec >= SECONDS_PER_MINUTE) {
        unit = 'minutes';
        divisor = SECONDS_PER_MINUTE;
    }
    const data = points.map(p => ({
        date: p.date,
        value: Math.round((p.secondsTotal / divisor) * 100) / 100,
        secondsTotal: p.secondsTotal,
    }));
    return { data, unit };
}

/**
 * Série para gráfico de área: tempo de limpeza por dia no mês atual (dia 1 → hoje).
 * Um ponto por dia com 0 quando não há dados.
 */
export function buildAreaSeriesByDayInCurrentMonthFromList(
    list,
    localeFormat = 'pt-BR',
    referenceDate = new Date()
) {
    const y = referenceDate.getFullYear();
    const mo = referenceDate.getMonth() + 1;
    const todayDom = referenceDate.getDate();
    const ymPrefix = `${y}-${pad2(mo)}-`;

    const dayKeys = [];
    for (let d = 1; d <= todayDom; d += 1) {
        dayKeys.push(`${ymPrefix}${pad2(d)}`);
    }

    const byDay = {};
    (list || []).forEach(item => {
        const day = getCleaningReportDayKey(
            getCleaningReportTaskStartTime(item)
        );
        if (day === '—' || !/^\d{4}-\d{2}-\d{2}$/.test(day)) return;
        if (!day.startsWith(ymPrefix)) return;
        byDay[day] = (byDay[day] || 0) + (Number(item.clean_time) || 0);
    });

    const points = dayKeys.map(day => ({
        date: cleaningReportYmdToChartLabel(day, localeFormat),
        secondsTotal: byDay[day] || 0,
    }));
    const maxSec = Math.max(0, ...points.map(p => p.secondsTotal));
    let unit = 'seconds';
    let divisor = 1;
    if (maxSec >= SECONDS_PER_HOUR) {
        unit = 'hours';
        divisor = SECONDS_PER_HOUR;
    } else if (maxSec >= SECONDS_PER_MINUTE) {
        unit = 'minutes';
        divisor = SECONDS_PER_MINUTE;
    }
    const data = points.map(p => ({
        date: p.date,
        value: Math.round((p.secondsTotal / divisor) * 100) / 100,
        secondsTotal: p.secondsTotal,
    }));
    return { data, unit };
}

/**
 * Série por dia: últimos 30 dias (inclusive hoje), com 0 nos dias sem dados.
 */
export function buildAreaSeriesByDayLast30DaysFromList(
    list,
    localeFormat = 'pt-BR',
    referenceDate = new Date()
) {
    const dayKeys = [];
    for (let i = 29; i >= 0; i -= 1) {
        dayKeys.push(format(subDays(referenceDate, i), 'yyyy-MM-dd'));
    }
    const daySet = new Set(dayKeys);

    const byDay = {};
    (list || []).forEach(item => {
        const day = getCleaningReportDayKey(
            getCleaningReportTaskStartTime(item)
        );
        if (day === '—' || !/^\d{4}-\d{2}-\d{2}$/.test(day)) return;
        if (!daySet.has(day)) return;
        byDay[day] = (byDay[day] || 0) + (Number(item.clean_time) || 0);
    });

    const points = dayKeys.map(day => ({
        date: cleaningReportYmdToChartLabel(day, localeFormat),
        secondsTotal: byDay[day] || 0,
    }));
    const maxSec = Math.max(0, ...points.map(p => p.secondsTotal));
    let unit = 'seconds';
    let divisor = 1;
    if (maxSec >= SECONDS_PER_HOUR) {
        unit = 'hours';
        divisor = SECONDS_PER_HOUR;
    } else if (maxSec >= SECONDS_PER_MINUTE) {
        unit = 'minutes';
        divisor = SECONDS_PER_MINUTE;
    }
    const data = points.map(p => ({
        date: p.date,
        value: Math.round((p.secondsTotal / divisor) * 100) / 100,
        secondsTotal: p.secondsTotal,
    }));
    return { data, unit };
}

/**
 * Por dia: soma de clean_area (área efetivamente limpa) vs soma de task_area (área planejada da tarefa).
 * Permite comparar desempenho diário.
 */
export function buildCleanAreaVsTaskAreaByDay(
    list,
    localeFormat = 'pt-BR',
    periodDates = null
) {
    if (!list || !list.length) return [];

    let isSingleDay = false;
    if (periodDates && periodDates.startDate && periodDates.endDate) {
        if (periodDates.startDate === periodDates.endDate) {
            isSingleDay = true;
        }
    }

    if (isSingleDay) {
        const byHour = new Array(24)
            .fill(null)
            .map(() => ({ clean_area: 0, task_area: 0 }));
        list.forEach(item => {
            // eslint-disable-next-line no-use-before-define
            const h = extractHourFromCreateTime(
                getCleaningReportTaskStartTime(item)
            );
            if (h == null) return;
            byHour[h].clean_area += Number(item.clean_area) || 0;
            byHour[h].task_area += Number(item.task_area) || 0;
        });

        return byHour.map((area, hour) => ({
            date: `${pad2(hour)}h`,
            clean_area: Math.round(area.clean_area * 100) / 100,
            task_area: Math.round(area.task_area * 100) / 100,
        }));
    }

    const byDay = {};
    list.forEach(item => {
        const day = getCleaningReportDayKey(
            getCleaningReportTaskStartTime(item)
        );
        if (day === '—') return;
        if (!byDay[day]) {
            byDay[day] = { clean_area: 0, task_area: 0 };
        }
        byDay[day].clean_area += Number(item.clean_area) || 0;
        byDay[day].task_area += Number(item.task_area) || 0;
    });
    const sortedDays = Object.keys(byDay).sort();
    return sortedDays.map(day => {
        const { clean_area: ca, task_area: ta } = byDay[day];
        return {
            date: cleaningReportYmdToChartLabel(day, localeFormat),
            clean_area: Math.round(ca * 100) / 100,
            task_area: Math.round(ta * 100) / 100,
        };
    });
}

/**
 * Hora 0–23 extraída de create_time (literal na string; timestamp numérico em UTC).
 */
export function extractHourFromCreateTime(createTime) {
    if (createTime == null) {
        return null;
    }
    if (typeof createTime === 'number' && Number.isFinite(createTime)) {
        const ms = createTime > 1e12 ? createTime : createTime * 1000;
        const d = new Date(ms);
        if (Number.isNaN(d.getTime())) {
            return null;
        }
        return d.getHours();
    }
    const s = String(createTime).trim();
    if (!s) {
        return null;
    }
    if (/^\d{1,2}$/.test(s)) {
        const hourOnly = Number(s);
        if (hourOnly >= 0 && hourOnly <= 23) return hourOnly;
    }
    if (/^\d{10,13}$/.test(s)) {
        const n = Number(s);
        const ms = n > 1e12 ? n : n * 1000;
        const d = new Date(ms);
        if (Number.isNaN(d.getTime())) return null;
        return d.getHours();
    }
    if (s.includes('T')) {
        const tail = (s.split('T')[1] || '').replace(/[Zz].*$/, '').trim();
        const m = /^(\d{1,2})/.exec(tail);
        if (m) {
            const h = parseInt(m[1], 10);
            return h >= 0 && h <= 23 ? h : null;
        }
    }
    const spaceIdx = s.search(/\s/);
    if (spaceIdx < 0) {
        return null;
    }
    const rest = s.slice(spaceIdx + 1).trim();
    const m2 = /^(\d{1,2})/.exec(rest);
    if (m2) {
        const h = parseInt(m2[1], 10);
        return h >= 0 && h <= 23 ? h : null;
    }
    return null;
}

/**
 * Soma de clean_area por hora do dia (0h–23h) conforme start_time da tarefa.
 */
export function buildCleanAreaByHourOfDay(list) {
    const byHour = new Array(24).fill(0);
    if (list && list.length) {
        list.forEach(item => {
            const h = extractHourFromCreateTime(
                getCleaningReportTaskStartTime(item)
            );
            if (h == null) {
                return;
            }
            byHour[h] += Number(item.clean_area) || 0;
        });
    }
    return byHour.map((area, hour) => ({
        hour,
        label: `${pad2(hour)}h`,
        clean_area: Math.round(area * 100) / 100,
    }));
}

/** Largura de cada coluna do heatmap de horário da frota. */
export const FLEET_SCHEDULE_BUCKET_HOURS = 2;

function taskLocalDate(row) {
    const start = getCleaningReportTaskStartTime(row);
    if (start == null) return null;
    if (typeof start === 'number' && Number.isFinite(start)) {
        const ms = start > 1e12 ? start : start * 1000;
        const d = new Date(ms);
        return Number.isNaN(d.getTime()) ? null : d;
    }
    const hour = extractHourFromCreateTime(start);
    const dayKey = getCleaningReportDayKey(start);
    if (!dayKey || dayKey === '—' || hour == null) return null;
    const parts = dayKey.split('-').map(x => parseInt(x, 10));
    if (parts.length !== 3 || parts.some(n => Number.isNaN(n))) return null;
    const d = new Date(parts[0], parts[1] - 1, parts[2], hour, 0, 0, 0);
    return Number.isNaN(d.getTime()) ? null : d;
}

function mondayIndexFromDate(date) {
    return (date.getDay() + 6) % 7;
}

export function getFleetScheduleHourLabels() {
    const labels = [];
    for (let hour = 0; hour < 24; hour += FLEET_SCHEDULE_BUCKET_HOURS) {
        labels.push(String(hour));
    }
    return labels;
}

export function fleetScheduleHourRangeLabel(bucket) {
    const start = Number(bucket) * FLEET_SCHEDULE_BUCKET_HOURS;
    const end = start + FLEET_SCHEDULE_BUCKET_HOURS;
    return `${start}–${end}h`;
}

/**
 * Grade 7×12 (dia da semana × faixas de 2 h) no período já filtrado em `list`.
 * Soma o tempo de limpeza; se ninguém mandou duração, usa contagem de tarefas.
 * A escala de cor é relativa ao máximo da grade (hoje e 90 dias continuam legíveis).
 */
export function buildFleetActivityHeatmap(list) {
    const rowCount = 7;
    const colCount = 24 / FLEET_SCHEDULE_BUCKET_HOURS;
    const grid = Array.from({ length: rowCount }, () =>
        Array.from({ length: colCount }, () => ({ seconds: 0, tasks: 0 }))
    );

    (Array.isArray(list) ? list : []).forEach(item => {
        const date = taskLocalDate(item);
        if (!date) return;
        const weekday = mondayIndexFromDate(date);
        const bucket = Math.floor(
            date.getHours() / FLEET_SCHEDULE_BUCKET_HOURS
        );
        if (weekday < 0 || weekday >= rowCount) return;
        if (bucket < 0 || bucket >= colCount) return;
        const cell = grid[weekday][bucket];
        cell.seconds += Math.max(0, Number(item.clean_time) || 0);
        cell.tasks += 1;
    });

    let maxSeconds = 0;
    let maxTasks = 0;
    grid.forEach(row => {
        row.forEach(cell => {
            if (cell.seconds > maxSeconds) maxSeconds = cell.seconds;
            if (cell.tasks > maxTasks) maxTasks = cell.tasks;
        });
    });

    const useSeconds = maxSeconds > 0;
    let maxHeat = 0;
    let peak = null;
    grid.forEach((row, weekday) => {
        row.forEach((cell, bucket) => {
            const heat = useSeconds ? cell.seconds : cell.tasks;
            cell.heat = heat;
            if (heat > maxHeat) {
                maxHeat = heat;
                peak = {
                    weekday,
                    bucket,
                    seconds: cell.seconds,
                    tasks: cell.tasks,
                    heat,
                };
            }
        });
    });

    return {
        grid,
        maxHeat,
        maxSeconds,
        maxTasks,
        useSeconds,
        peak,
        bucketHours: FLEET_SCHEDULE_BUCKET_HOURS,
        hourLabels: getFleetScheduleHourLabels(),
    };
}

function parseCleaningReportYmd(ymd) {
    if (!ymd || typeof ymd !== 'string') return null;
    const parts = ymd.split('-').map(x => parseInt(x, 10));
    if (parts.length !== 3 || parts.some(n => Number.isNaN(n))) return null;
    return new Date(parts[0], parts[1] - 1, parts[2]);
}

/** Dias no intervalo do filtro de período (startDate → endDate, inclusive). */
export function getCleaningReportPeriodDaysCount(periodDates) {
    if (!periodDates || !periodDates.startDate || !periodDates.endDate)
        return 1;
    const start = parseCleaningReportYmd(periodDates.startDate);
    const end = parseCleaningReportYmd(periodDates.endDate);
    if (!start || !end || end < start) return 1;
    return differenceInDays(end, start) + 1;
}

/** O filtro cobre pelo menos um mês de calendário (ex.: 15/01 → 15/02). */
export function cleaningReportFilterCoversAtLeastOneMonth(periodDates) {
    if (!periodDates || !periodDates.startDate || !periodDates.endDate)
        return false;
    const start = parseCleaningReportYmd(periodDates.startDate);
    const end = parseCleaningReportYmd(periodDates.endDate);
    if (!start || !end || end < start) return false;
    const oneMonthAfterStart = new Date(
        start.getFullYear(),
        start.getMonth() + 1,
        start.getDate()
    );
    return end >= oneMonthAfterStart;
}

/** Índices fixos: 0 horas, 1 dias da semana, 2 dias, 3 meses. */
export const CLEANING_REPORT_CHART_PERIOD_INDICES = [0, 1, 2, 3];

/** Se a opção do seletor de métrica analisada deve aparecer para o filtro atual. */
export function isCleaningReportChartPeriodVisible(
    periodIdx,
    periodDates,
    daysCount
) {
    if (periodIdx === 0) return true;
    if (periodIdx === 2) return daysCount >= 2;
    if (periodIdx === 1) return daysCount >= 7;
    if (periodIdx === 3) {
        return cleaningReportFilterCoversAtLeastOneMonth(periodDates);
    }
    return false;
}

export function getVisibleCleaningReportChartPeriodIndices(
    periodDates,
    daysCount
) {
    return CLEANING_REPORT_CHART_PERIOD_INDICES.filter(idx =>
        isCleaningReportChartPeriodVisible(idx, periodDates, daysCount)
    );
}

/** Índice padrão do seletor entre as opções visíveis. */
export function getCleaningReportDefaultPeriodIdx(periodDates, daysCount) {
    const visible = getVisibleCleaningReportChartPeriodIndices(
        periodDates,
        daysCount
    );
    let preferred = 0;
    if (cleaningReportFilterCoversAtLeastOneMonth(periodDates)) preferred = 3;
    else if (daysCount >= 7) preferred = 2;
    else if (daysCount >= 2) preferred = 2;
    else preferred = 0;

    if (visible.includes(preferred)) return preferred;
    const fallback = visible[visible.length - 1];
    return fallback != null ? fallback : 0;
}

/** Mantém a seleção do usuário ou volta ao padrão se a opção deixou de ser válida. */
export function resolveCleaningReportChartPeriodIdx(
    selectedPeriodIdx,
    periodDates,
    daysCount
) {
    const defaultIdx = getCleaningReportDefaultPeriodIdx(
        periodDates,
        daysCount
    );
    if (selectedPeriodIdx == null) return defaultIdx;
    const idx = Number(selectedPeriodIdx);
    if (
        Number.isNaN(idx) ||
        !isCleaningReportChartPeriodVisible(idx, periodDates, daysCount)
    ) {
        return defaultIdx;
    }
    return idx;
}

function scaleCleanTimeChartPoints(points) {
    const maxSec = Math.max(0, ...points.map(p => p.secondsTotal));
    let unit = 'seconds';
    let divisor = 1;
    if (maxSec >= SECONDS_PER_HOUR) {
        unit = 'hours';
        divisor = SECONDS_PER_HOUR;
    } else if (maxSec >= SECONDS_PER_MINUTE) {
        unit = 'minutes';
        divisor = SECONDS_PER_MINUTE;
    }
    const data = points.map(p => ({
        name: p.name,
        value: Math.round((p.secondsTotal / divisor) * 100) / 100,
        secondsTotal: p.secondsTotal,
    }));
    return { data, unit };
}

export function getCleaningReportWeekDayLabels(localeFormat) {
    const formatter = new Intl.DateTimeFormat(localeFormat, {
        weekday: 'short',
    });
    return Array.from({ length: 7 }, (_, i) =>
        formatter.format(new Date(2024, 0, 7 + i))
    );
}

export function getCleaningReportMonthLabels(localeFormat) {
    const formatter = new Intl.DateTimeFormat(localeFormat, { month: 'short' });
    return Array.from({ length: 12 }, (_, i) =>
        formatter.format(new Date(2024, i, 1))
    );
}

const CLEAN_TIME_CHART_LEGEND_LABELS = {
    pt_BR: 'Tempo de limpeza',
    en_US: 'Cleaning time',
    en_GB: 'Cleaning time',
    es_ES: 'Tiempo de limpieza',
};

export function getCleanTimeChartLegendLabel(localeCode = 'pt_BR') {
    return (
        CLEAN_TIME_CHART_LEGEND_LABELS[localeCode] ||
        CLEAN_TIME_CHART_LEGEND_LABELS.pt_BR
    );
}

/** Formata ticks do eixo Y do gráfico de tempo de limpeza. */
export function formatCleanTimeChartAxisTick(v, unit) {
    if (v == null || Number.isNaN(Number(v))) return '';
    const n = Number(v);
    if (unit === 'seconds') return `${Math.round(n)} s`;
    if (unit === 'minutes') {
        return Number.isInteger(n) ? `${n} min` : `${n.toFixed(1)} min`;
    }
    return Number.isInteger(n) ? `${n} h` : `${n.toFixed(2)} h`;
}

export function summarizeList(list) {
    if (!list || !list.length) {
        return {
            totalCleanTimeSec: 0,
            totalCleanArea: 0,
            taskCount: 0,
        };
    }
    return {
        totalCleanTimeSec: list.reduce(
            (s, i) => s + (Number(i.clean_time) || 0),
            0
        ),
        totalCleanArea: list.reduce(
            (s, i) => s + (Number(i.clean_area) || 0),
            0
        ),
        taskCount: list.length,
    };
}

/**
 * Campos da API que podem vir como string JSON (config, floor_list).
 * @returns {object|Array|null}
 */
export function parseCleaningReportJsonField(raw) {
    if (raw == null || raw === '') return null;
    if (typeof raw === 'object') return raw;
    if (typeof raw !== 'string') return null;
    try {
        return JSON.parse(raw);
    } catch (e) {
        return null;
    }
}

/**
 * Unix (segundos) → data/hora local.
 */
export function formatCleaningReportUnixSeconds(
    unixSec,
    localeFormat = 'pt-BR'
) {
    const n = Number(unixSec);
    if (!Number.isFinite(n)) return '—';
    const d = new Date(n * 1000);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString(localeFormat, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    });
}

/** Locale Redux (`pt_BR`) → BCP 47 para `Intl` / gráficos. */
export function localeToBcp47(code) {
    if (!code) return 'pt-BR';
    const map = { pt_BR: 'pt-BR', en_US: 'en-US', en_GB: 'en-GB' };
    return map[code] || 'pt-BR';
}

/**
 * Iniciais dos dias, segunda → domingo, no locale do usuário.
 * Âncora: 2026-01-05 é segunda.
 */
export function getWeekdayNarrowLabels(localeCode = 'pt_BR') {
    const loc = localeToBcp47(localeCode);
    return Array.from({ length: 7 }, (_, index) => {
        const date = new Date(2026, 0, 5 + index);
        return new Intl.DateTimeFormat(loc, { weekday: 'narrow' }).format(date);
    });
}

export function getWeekdayLongLabel(mondayIndex, localeCode = 'pt_BR') {
    const loc = localeToBcp47(localeCode);
    const index = Number(mondayIndex);
    const safe = Number.isFinite(index) ? index : 0;
    const date = new Date(2026, 0, 5 + safe);
    return new Intl.DateTimeFormat(loc, { weekday: 'long' }).format(date);
}

/** Mensagem de erro a partir de `err.response.data` (axios), p.ex. detalhe do modal. */
export function cleaningDetailErrorMessageFromPayload(payload) {
    if (payload && typeof payload === 'object' && payload.error) {
        return String(payload.error);
    }
    if (typeof payload === 'string') {
        return payload;
    }
    return '';
}

export function defaultCleaningReportDateRange() {
    const today = format(new Date(), 'yyyy-MM-dd');
    return {
        startDate: today,
        endDate: today,
    };
}

export function cleaningReportDefaultShortcut(startYmd, endYmd) {
    const until = new Date(`${endYmd}T12:00:00`);
    const from = new Date(`${startYmd}T12:00:00`);
    const today = new Date();
    if (Number.isNaN(until.getTime()) || Number.isNaN(from.getTime())) {
        return 'custom';
    }
    if (differenceInDays(until, today) !== 0) return 'custom';
    const diff = differenceInDays(until, from);
    if (diff === 0) return 'today';
    if (diff === 30 || diff === 60 || diff === 90) return `${diff}_days`;
    return 'custom';
}

/** IDs i18n das opções do seletor de métrica analisada (Horas → Meses). */
export const CLEANING_REPORT_CHART_PERIOD_MSG_IDS = [
    'page.robots.cleaning_report.chart.period.hours',
    'page.robots.cleaning_report.chart.period.weekdays',
    'page.robots.cleaning_report.chart.period.days',
    'page.robots.cleaning_report.chart.period.months',
];

/**
 * Cores alinhadas ao tema do pluginspace (ColorList).
 * @param {object} colors — resultado de `GetPluginspaceTheme(theme)`
 */
function normalizeCssColor(color) {
    if (!color || typeof color !== 'string') return '';
    return color.trim().toLowerCase();
}

function siblingChartColor(base) {
    try {
        return getLuminance(base) > 0.35
            ? darken(0.16, base)
            : lighten(0.2, base);
    } catch (err) {
        return '#b45309';
    }
}

export function getCleaningReportChartColors(colors = {}) {
    const graphColor = colors.dashboard_graphs || colors.primary || '#3f51b5';
    const cardColor =
        colors.dashboard_cards || colors.dashboard_graphs || '#3f51b5';
    const secondary = colors.secondary || '#E91E63';
    const pieSegmentColors = [
        graphColor,
        cardColor,
        secondary,
        colors.primary || '#3f51b5',
    ];
    const waterColor = graphColor;
    let energyResolved = cardColor;
    if (normalizeCssColor(energyResolved) === normalizeCssColor(waterColor)) {
        energyResolved = siblingChartColor(waterColor);
    }
    return {
        graphColor,
        cardColor,
        pieSegmentColors,
        waterColor,
        energyColor: energyResolved,
        dryColor: energyResolved,
        wetColor: waterColor,
    };
}

export function scrollCleaningReportTableIntoView(tableRef) {
    if (!tableRef || !tableRef.current) return;
    tableRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
    });
}

/**
 * Constrói a lista de dados a ser injetada no componente de exportação CSV.
 * Mapeia os campos da tabela e os ocultos requisitados.
 * @param {Array} list - Lista completa de limpezas (reportPayload.list)
 * @param {string} localeCode - Código do idioma (ex: 'pt_BR')
 * @param {boolean} includeRobotInfo - Se deve incluir o nome do robô (para Pluginspace)
 */
export function buildCleaningReportCsvData(
    list,
    localeCode = 'pt_BR',
    includeRobotInfo = false,
    isDetailed = false
) {
    if (!list || !Array.isArray(list)) {
        return [];
    }

    return list.map(item => {
        const rowData = {
            report_id: item.report_id != null ? String(item.report_id) : '',
            task_name: item.task_name || '—',
            clean_time_fmt: formatCleaningDuration(item.clean_time),
            efficiency:
                Number(item.clean_area || 0) > 0 &&
                Number(item.clean_time || 0) > 0
                    ? (
                          Number(item.clean_area) /
                          Math.max(1, Number(item.clean_time) / 60)
                      ).toFixed(2)
                    : 'N/A',
            clean_area: Number(item.clean_area || 0).toFixed(2),
            task_area: Number(item.task_area || 0).toFixed(2),
            create_time_fmt: formatCleaningReportCreateDateTime(
                item.end_time || item.start_time || item.create_time
            ),
            mode_label: getCleaningModeLabel(item.mode, localeCode),
            status_label: getCleaningTaskStatusLabel(item.status, localeCode),
        };

        if (includeRobotInfo) {
            const robotName =
                item._robot && item._robot.name ? item._robot.name : '';
            const robotCode =
                item._robot && item._robot.code ? ` (${item._robot.code})` : '';
            rowData.robot_name = `${robotName}${robotCode}`.trim() || '—';
        }

        if (isDetailed) {
            const bcp = localeToBcp47(localeCode);
            rowData.start_time_fmt = formatCleaningReportUnixSeconds(
                item.start_time,
                bcp
            );
            rowData.end_time_fmt = formatCleaningReportUnixSeconds(
                item.end_time,
                bcp
            );

            const completionPct =
                item.percentage != null ? Number(item.percentage) : 0;
            rowData.completion_pct = `${Math.round(
                Math.min(100, Math.max(0, completionPct))
            )}%`;

            let batteryPct = null;
            if (item.battery != null) {
                batteryPct = Number(item.battery);
            } else if (item.cost_battery != null) {
                batteryPct = Number(item.cost_battery);
            }
            rowData.battery_pct =
                batteryPct != null && !Number.isNaN(batteryPct)
                    ? `${Math.round(Math.min(100, Math.max(0, batteryPct)))}%`
                    : '—';
        }

        return rowData;
    });
}

/**
 * Retorna as configurações das colunas (cabeçalhos) para o CSV de Relatório de Limpeza.
 * @param {object} intl - Objeto intl para chamadas de formatação (injectIntl)
 * @param {boolean} includeRobotInfo - Se a coluna de robô deve ser inserida (para Pluginspace)
 * @param {boolean} isDetailed - Se as colunas de detalhamento extra devem ser incluídas
 */
export function getCleaningReportCsvHeaders(
    intl,
    includeRobotInfo = false,
    isDetailed = false
) {
    const headers = [
        {
            label: intl.formatMessage({
                id: 'page.robots.cleaning_report.csv.report_id',
                defaultMessage: 'Report ID',
            }),
            key: 'report_id',
        },
    ];

    if (includeRobotInfo) {
        headers.push({
            label: intl.formatMessage({
                id: 'page.robots.cleaning_report.csv.col_robot',
                defaultMessage: 'Robot',
            }),
            key: 'robot_name',
        });
    }

    headers.push(
        {
            label: intl.formatMessage({
                id: 'page.robots.cleaning_report.csv.col_task',
                defaultMessage: 'Task',
            }),
            key: 'task_name',
        },
        {
            label: intl.formatMessage({
                id: 'page.robots.cleaning_report.csv.col_clean_time',
                defaultMessage: 'Clean Time',
            }),
            key: 'clean_time_fmt',
        },
        {
            label: 'Eficiência (m²/min)',
            key: 'efficiency',
        },
        {
            label: intl.formatMessage({
                id: 'page.robots.cleaning_report.csv.col_area',
                defaultMessage: 'Clean Area',
            }),
            key: 'clean_area',
        },
        {
            label: intl.formatMessage({
                id: 'page.robots.cleaning_report.csv.col_created',
                defaultMessage: 'Created At',
            }),
            key: 'create_time_fmt',
        },
        {
            label: intl.formatMessage({
                id: 'page.robots.cleaning_report.csv.col_mode',
                defaultMessage: 'Mode',
            }),
            key: 'mode_label',
        },
        {
            label: intl.formatMessage({
                id: 'page.robots.cleaning_report.csv.col_status',
                defaultMessage: 'Status',
            }),
            key: 'status_label',
        }
    );

    if (isDetailed) {
        headers.push(
            {
                label: intl.formatMessage({
                    id: 'page.robots.cleaning_report.detail.metric.start',
                    defaultMessage: 'Start Time',
                }),
                key: 'start_time_fmt',
            },
            {
                label: intl.formatMessage({
                    id: 'page.robots.cleaning_report.detail.metric.end',
                    defaultMessage: 'End Time',
                }),
                key: 'end_time_fmt',
            },
            {
                label: intl.formatMessage({
                    id: 'page.robots.cleaning_report.detail.completion',
                    defaultMessage: 'Completion (%)',
                }),
                key: 'completion_pct',
            },
            {
                label: intl.formatMessage({
                    id:
                        'page.robots.cleaning_report.detail.battery_consumption',
                    defaultMessage: 'Battery Consumption',
                }),
                key: 'battery_pct',
            }
        );
    }

    return headers;
}

/**
 * Série para gráfico de tempo de limpeza com agregação por período
 * (mesmo modelo de granularidade do card de ROI).
 */
export function buildCleanTimeChartData(
    cleaningReports,
    periodIdx,
    periodDates,
    localeCode = 'pt_BR'
) {
    const list = cleaningReports || [];
    const localeFormat = localeToBcp47(localeCode);

    if (periodIdx === 0) {
        const hourlyTime = new Array(24).fill(0);
        list.forEach(item => {
            const h = extractHourFromCreateTime(
                getCleaningReportTaskStartTime(item)
            );
            if (h != null && h >= 0 && h <= 23) {
                hourlyTime[h] += Number(item.clean_time) || 0;
            }
        });
        return scaleCleanTimeChartPoints(
            Array.from({ length: 24 }, (_, i) => ({
                name: `${i}h`,
                secondsTotal: hourlyTime[i],
            }))
        );
    }

    if (periodIdx === 1) {
        const dowTime = new Array(7).fill(0);
        list.forEach(item => {
            const dayKey = getCleaningReportDayKey(
                getCleaningReportTaskStartTime(item)
            );
            if (dayKey !== '—') {
                const [y, m, d] = dayKey.split('-');
                const dObj = new Date(Number(y), Number(m) - 1, Number(d));
                dowTime[dObj.getDay()] += Number(item.clean_time) || 0;
            }
        });
        return scaleCleanTimeChartPoints(
            getCleaningReportWeekDayLabels(localeFormat).map((label, i) => ({
                name: label,
                secondsTotal: dowTime[i],
            }))
        );
    }

    if (periodIdx === 2) {
        const byDayTime = {};

        if (periodDates && periodDates.startDate && periodDates.endDate) {
            const parseDateString = dStr => {
                const parts = dStr.split('T')[0].split('-');
                return new Date(
                    Number(parts[0]),
                    Number(parts[1]) - 1,
                    Number(parts[2])
                );
            };
            const current = parseDateString(periodDates.startDate);
            const end = parseDateString(periodDates.endDate);
            let safety = 0;
            while (current <= end && safety < 1000) {
                const y = current.getFullYear();
                const m = String(current.getMonth() + 1).padStart(2, '0');
                const day = String(current.getDate()).padStart(2, '0');
                byDayTime[`${y}-${m}-${day}`] = 0;
                current.setDate(current.getDate() + 1);
                safety += 1;
            }
        }

        list.forEach(item => {
            const dayKey = getCleaningReportDayKey(
                getCleaningReportTaskStartTime(item)
            );
            if (dayKey !== '—') {
                byDayTime[dayKey] =
                    (byDayTime[dayKey] || 0) + (Number(item.clean_time) || 0);
            }
        });

        const sortedDays = Object.keys(byDayTime).sort();
        return scaleCleanTimeChartPoints(
            sortedDays.map(key => {
                const [y, m, d] = key.split('-');
                const dateObj = new Date(Number(y), Number(m) - 1, Number(d));
                return {
                    name: dateObj.toLocaleDateString(localeFormat, {
                        day: '2-digit',
                        month: '2-digit',
                    }),
                    secondsTotal: byDayTime[key],
                };
            })
        );
    }

    const monthTime = new Array(12).fill(0);
    list.forEach(item => {
        const dayKey = getCleaningReportDayKey(
            getCleaningReportTaskStartTime(item)
        );
        if (dayKey !== '—') {
            const m = Number(dayKey.split('-')[1]);
            monthTime[m - 1] += Number(item.clean_time) || 0;
        }
    });
    return scaleCleanTimeChartPoints(
        getCleaningReportMonthLabels(localeFormat).map((label, i) => ({
            name: label,
            secondsTotal: monthTime[i],
        }))
    );
}

function roundConsumption(n) {
    return Math.round(Number(n) * 10000) / 10000;
}

function roundPct(n) {
    return Math.round(Number(n) * 10) / 10;
}

export function hasMeasuredConsumptionValue(value) {
    return (
        value !== undefined &&
        value !== null &&
        value !== '' &&
        Number.isFinite(Number(value))
    );
}

/**
 * Consome totais Pudu (água/energia) e reparte por modo da listagem.
 * Água: 100% lavagem molhada (mode=1). Energia: pelo tempo de cada tipo.
 * A API de análise não quebra por modo; tempo de missão é o melhor proxy
 * (CC1 Pro: scrubbing e sweeping têm a mesma autonomia ~5 h).
 */
export function buildConsumptionByCleaningMode(
    list,
    consumption,
    localeCode = 'pt_BR'
) {
    const buckets = {
        2: { time: 0, tasks: 0 },
        1: { time: 0, tasks: 0 },
    };
    (Array.isArray(list) ? list : []).forEach(item => {
        const mode = Number(item && item.mode);
        if (mode !== 1 && mode !== 2) return;
        buckets[mode].tasks += 1;
        buckets[mode].time += Number(item.clean_time) || 0;
    });

    const totalTime = buckets[1].time + buckets[2].time;
    const totalTasks = buckets[1].tasks + buckets[2].tasks;
    const hasAnalysis = !!(
        consumption && consumption.source === 'pudu_analysis'
    );

    let waterMl = 0;
    let energyKwh = 0;
    let dryEnergySum = 0;
    let wetEnergySum = 0;
    let wetWaterSum = 0;
    let hasExplicitEnergy = false;

    if (hasAnalysis) {
        waterMl = Number(consumption.water_ml) || 0;
        energyKwh = Number(consumption.energy_kwh) || 0;
    } else {
        (Array.isArray(list) ? list : []).forEach(item => {
            const mode = Number(item && item.mode);
            if (hasMeasuredConsumptionValue(item && item.power_consumption)) {
                hasExplicitEnergy = true;
                const p = Number(item.power_consumption) || 0;
                if (mode === 2) dryEnergySum += p;
                if (mode === 1) wetEnergySum += p;
                energyKwh += p;
            }
            if (
                mode === 1 &&
                hasMeasuredConsumptionValue(item && item.cost_water)
            ) {
                const w = Number(item.cost_water) || 0;
                wetWaterSum += w;
                waterMl += w;
            }
        });
    }

    const shareOf = mode => {
        if (totalTime > 0) return buckets[mode].time / totalTime;
        if (totalTasks > 0) return buckets[mode].tasks / totalTasks;
        return 0;
    };

    const dryEnergy = hasExplicitEnergy ? dryEnergySum : energyKwh * shareOf(2);
    const wetEnergy = hasExplicitEnergy ? wetEnergySum : energyKwh * shareOf(1);
    const wetWater = !hasAnalysis && wetWaterSum > 0 ? wetWaterSum : waterMl;

    const energyPct = value =>
        energyKwh > 0 ? roundPct((value / energyKwh) * 100) : 0;
    const waterPct = value =>
        waterMl > 0 ? roundPct((value / waterMl) * 100) : 0;

    return [2, 1].map(mode => {
        const isWet = mode === 1;
        const key = isWet ? 'wet' : 'dry';
        const energy = isWet ? wetEnergy : dryEnergy;
        const water = isWet ? wetWater : 0;
        return {
            key,
            mode,
            name: getCleaningModeLabel(mode, localeCode),
            tasks: buckets[mode].tasks,
            water: roundConsumption(water / 1000),
            energy: roundConsumption(energy),
            waterPct: waterPct(water),
            energyPct: energyPct(energy),
        };
    });
}

/**
 * Totais de água (ml) e energia (kWh) a partir do detalhe persistido.
 * Tarefas ainda sem detalhe não entram na soma.
 */
export function summarizeCleaningConsumption(list) {
    const rows = Array.isArray(list) ? list : [];
    let waterMl = 0;
    let energyKwh = 0;
    let measuredWater = 0;
    let measuredEnergy = 0;

    rows.forEach(item => {
        if (hasMeasuredConsumptionValue(item && item.cost_water)) {
            waterMl += Number(item.cost_water) || 0;
            measuredWater += 1;
        }
        if (hasMeasuredConsumptionValue(item && item.power_consumption)) {
            energyKwh += Number(item.power_consumption) || 0;
            measuredEnergy += 1;
        }
    });

    return {
        waterMl,
        energyKwh,
        measuredWater,
        measuredEnergy,
        taskCount: rows.length,
    };
}

export function dayKeysInPeriod(periodDates) {
    const keys = [];
    if (!periodDates || !periodDates.startDate || !periodDates.endDate) {
        return keys;
    }
    const partsOf = dStr =>
        String(dStr)
            .split('T')[0]
            .split('-');
    const startParts = partsOf(periodDates.startDate);
    const endParts = partsOf(periodDates.endDate);
    const current = new Date(
        Number(startParts[0]),
        Number(startParts[1]) - 1,
        Number(startParts[2])
    );
    const end = new Date(
        Number(endParts[0]),
        Number(endParts[1]) - 1,
        Number(endParts[2])
    );
    let safety = 0;
    while (current <= end && safety < 1000) {
        keys.push(
            `${current.getFullYear()}-${pad2(current.getMonth() + 1)}-${pad2(
                current.getDate()
            )}`
        );
        current.setDate(current.getDate() + 1);
        safety += 1;
    }
    return keys;
}

function formatDayAxisLabel(dayKey, localeFormat) {
    const [y, m, d] = String(dayKey).split('-');
    const dateObj = new Date(Number(y), Number(m) - 1, Number(d));
    if (Number.isNaN(dateObj.getTime())) return dayKey;
    return dateObj.toLocaleDateString(localeFormat, {
        day: '2-digit',
        month: '2-digit',
    });
}

/**
 * Série diária (ou por hora se o período for 1 dia) de água em litros e energia em kWh.
 */
export function buildConsumptionChartSeries(
    list,
    periodDates,
    localeCode = 'pt_BR'
) {
    const rows = Array.isArray(list) ? list : [];
    const localeFormat = localeToBcp47(localeCode);
    const isSingleDay = !!(
        periodDates &&
        periodDates.startDate &&
        periodDates.endDate &&
        periodDates.startDate === periodDates.endDate
    );

    if (isSingleDay) {
        const waterHour = new Array(24).fill(0);
        const energyHour = new Array(24).fill(0);
        rows.forEach(item => {
            const h = extractHourFromCreateTime(
                getCleaningReportTaskStartTime(item)
            );
            if (h == null || h < 0 || h > 23) return;
            if (hasMeasuredConsumptionValue(item.cost_water)) {
                waterHour[h] += Number(item.cost_water) || 0;
            }
            if (hasMeasuredConsumptionValue(item.power_consumption)) {
                energyHour[h] += Number(item.power_consumption) || 0;
            }
        });
        return {
            water: waterHour.map((ml, hour) => ({
                name: `${pad2(hour)}h`,
                value: roundConsumption(ml / 1000),
            })),
            energy: energyHour.map((kwh, hour) => ({
                name: `${pad2(hour)}h`,
                value: roundConsumption(kwh),
            })),
        };
    }

    const seeded = dayKeysInPeriod(periodDates);
    const waterByDay = {};
    const energyByDay = {};
    seeded.forEach(key => {
        waterByDay[key] = 0;
        energyByDay[key] = 0;
    });

    rows.forEach(item => {
        const day = getCleaningReportDayKey(
            getCleaningReportTaskStartTime(item)
        );
        if (day === '—') return;
        if (hasMeasuredConsumptionValue(item.cost_water)) {
            waterByDay[day] =
                (waterByDay[day] || 0) + (Number(item.cost_water) || 0);
        }
        if (hasMeasuredConsumptionValue(item.power_consumption)) {
            energyByDay[day] =
                (energyByDay[day] || 0) + (Number(item.power_consumption) || 0);
        }
    });

    const sorted =
        seeded.length > 0
            ? seeded
            : Array.from(
                  new Set([
                      ...Object.keys(waterByDay),
                      ...Object.keys(energyByDay),
                  ])
              ).sort();

    return {
        water: sorted.map(key => ({
            name: formatDayAxisLabel(key, localeFormat),
            value: roundConsumption((waterByDay[key] || 0) / 1000),
        })),
        energy: sorted.map(key => ({
            name: formatDayAxisLabel(key, localeFormat),
            value: roundConsumption(energyByDay[key] || 0),
        })),
    };
}

export function formatEnergyKwh(kwh) {
    const n = Number(kwh) || 0;
    if (n >= 10) return `${n.toFixed(1)} kWh`;
    if (n >= 1) return `${n.toFixed(2)} kWh`;
    return `${n.toFixed(3)} kWh`;
}

export function seriesPeak(series) {
    let best = null;
    (series || []).forEach(point => {
        const value = Number(point && point.value) || 0;
        if (best == null || value > best.value) {
            best = { name: (point && point.name) || '', value };
        }
    });
    if (!best || !(best.value > 0)) return null;
    return best;
}

function formatAnalysisTick(taskTime, timeUnit, localeCode = 'pt_BR') {
    if (taskTime == null || taskTime === '') return '';
    const s = String(taskTime).trim();
    if (timeUnit === 'hour') {
        const hour = extractHourFromCreateTime(s);
        if (hour != null) return `${pad2(hour)}h`;
        return s;
    }
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
        const day = s.slice(0, 10);
        if (/^\d{4}-\d{2}-\d{2}$/.test(day)) {
            return formatDayAxisLabel(day, localeToBcp47(localeCode));
        }
    }
    return s;
}

function isHourlyConsumptionPeriod(consumption, periodDates) {
    if (consumption && consumption.time_unit === 'hour') return true;
    return !!(
        periodDates &&
        periodDates.startDate &&
        periodDates.endDate &&
        periodDates.startDate === periodDates.endDate
    );
}

function pointConsumptionValue(point, field) {
    if (field === 'water') {
        return roundConsumption((Number(point && point.water_ml) || 0) / 1000);
    }
    return roundConsumption(Number(point && point.energy_kwh) || 0);
}

/**
 * Série dos cards de água/energia a partir do agregado Pudu do período.
 * Em janela de 24h (atalho Hoje) agrupa por hora (00h–23h).
 */
export function seriesFromPuduAnalysis(
    consumption,
    field,
    localeCode = 'pt_BR',
    periodDates = null
) {
    const chart =
        consumption && Array.isArray(consumption.chart)
            ? consumption.chart
            : [];
    const timeUnit = consumption && consumption.time_unit;

    if (isHourlyConsumptionPeriod(consumption, periodDates)) {
        const byHour = new Array(24).fill(0);
        chart.forEach(point => {
            const hour = extractHourFromCreateTime(point && point.task_time);
            if (hour == null || hour < 0 || hour > 23) return;
            byHour[hour] += pointConsumptionValue(point, field);
        });
        return byHour.map((value, hour) => ({
            name: `${pad2(hour)}h`,
            value: roundConsumption(value),
        }));
    }

    return chart.map(point => ({
        name: formatAnalysisTick(point.task_time, timeUnit, localeCode),
        value: pointConsumptionValue(point, field),
    }));
}

/**
 * Configurações padrão de aplicação de limpeza (usadas quando o robô não possui aplicação vinculada).
 */
export const DEFAULT_CLEANING_APP_SETTINGS = {
    human_productivity: 150,
    discharge_rate: 20,
    tank_capacity: 3,
    manual_reference: 500,
    chemical_dilution_ratio: 10,
};

/**
 * Resolve as configurações de limpeza de cada robô selecionado e da frota agregada.
 * Garante que robôs sem aplicação assumam explicitamente os valores padrão do sistema.
 *
 * @param {Array<string|number>} selectedRobotIds Lista de IDs de robôs selecionados.
 * @param {Array<Object>} robots Lista de objetos de robôs.
 * @param {Object} appMap Mapa de configurações de aplicações indexado por ID/UID.
 * @returns {Object} Configurações da frota, array de diluições individuais e mapa de produtividade por robô.
 */
export function resolveFleetCleaningSettings(selectedRobotIds, robots, appMap) {
    if (!Array.isArray(selectedRobotIds) || selectedRobotIds.length === 0) {
        return {
            fleetSettings: { ...DEFAULT_CLEANING_APP_SETTINGS },
            robotDilutions: [],
            robotProductivityMap: {},
        };
    }

    const robotsList = Array.isArray(robots) ? robots : [];
    const applications = appMap && typeof appMap === 'object' ? appMap : {};

    let sumHumanProd = 0;
    let sumTankCapacity = 0;
    let sumDischargeRate = 0;
    let sumManualRef = 0;
    let sumDilution = 0;
    let count = 0;

    const robotDilutions = [];
    const robotProductivityMap = {};

    selectedRobotIds.forEach(rid => {
        const robotObj = robotsList.find(
            r => r && (r.id === rid || String(r.id) === String(rid))
        );
        const robotData =
            robotObj && typeof robotObj === 'object' ? robotObj : {};

        const appId =
            robotData.settings &&
            robotData.settings.applications &&
            robotData.settings.applications.cleaning;

        const rawSettings =
            appId && applications[appId] ? applications[appId] : null;
        const appSettings =
            rawSettings && rawSettings.settings
                ? rawSettings.settings
                : rawSettings;

        // Produtividade Humana (m²/h): fallback seguro para 150 se vazio, <= 0 ou inválido
        const humanProdRaw = appSettings && appSettings.human_productivity;
        const humanProdNum = parseFloat(humanProdRaw);
        const humanProd =
            Number.isFinite(humanProdNum) && humanProdNum > 0
                ? humanProdNum
                : DEFAULT_CLEANING_APP_SETTINGS.human_productivity;

        // Capacidade do Tanque (L)
        const tankCapacityRaw = appSettings && appSettings.tank_capacity;
        const tankCapacityNum = parseFloat(tankCapacityRaw);
        const tankCapacity =
            Number.isFinite(tankCapacityNum) && tankCapacityNum > 0
                ? tankCapacityNum
                : DEFAULT_CLEANING_APP_SETTINGS.tank_capacity;

        // Taxa de Descarga (ml/m²)
        const dischargeRateRaw = appSettings && appSettings.discharge_rate;
        const dischargeRateNum = parseFloat(dischargeRateRaw);
        const dischargeRate =
            Number.isFinite(dischargeRateNum) && dischargeRateNum > 0
                ? dischargeRateNum
                : DEFAULT_CLEANING_APP_SETTINGS.discharge_rate;

        // Referência Manual (ml/m²)
        const manualRefRaw = appSettings && appSettings.manual_reference;
        const manualRefNum = parseFloat(manualRefRaw);
        const manualRef =
            Number.isFinite(manualRefNum) && manualRefNum > 0
                ? manualRefNum
                : DEFAULT_CLEANING_APP_SETTINGS.manual_reference;

        // Diluição Química (ml/L)
        const dilutionRaw = appSettings && appSettings.chemical_dilution_ratio;
        const dilutionNum = parseFloat(dilutionRaw);
        const dilution =
            Number.isFinite(dilutionNum) && dilutionNum >= 0
                ? dilutionNum
                : DEFAULT_CLEANING_APP_SETTINGS.chemical_dilution_ratio;

        sumHumanProd += humanProd;
        sumTankCapacity += tankCapacity;
        sumDischargeRate += dischargeRate;
        sumManualRef += manualRef;
        sumDilution += dilution;
        count += 1;

        robotProductivityMap[rid] = humanProd;

        const extras =
            robotData.settings && robotData.settings.extras
                ? robotData.settings.extras
                : {};
        robotDilutions.push({
            robot_id: rid,
            sn: extras.sn || robotData.code || '',
            dilution_ml_per_l: dilution,
        });
    });

    const divisor = count > 0 ? count : 1;

    return {
        fleetSettings: {
            human_productivity: Number((sumHumanProd / divisor).toFixed(2)),
            tank_capacity: sumTankCapacity,
            discharge_rate: Number((sumDischargeRate / divisor).toFixed(2)),
            manual_reference: Number((sumManualRef / divisor).toFixed(2)),
            chemical_dilution_ratio: Number((sumDilution / divisor).toFixed(2)),
        },
        robotDilutions,
        robotProductivityMap,
    };
}

/**
 * Calcula a produtividade humana efetiva da frota ativa no período selecionado.
 * Pondera a taxa de produtividade de cada robô pelo tempo real de operação nas tarefas do relatório.
 * Se nenhum robô operou no período, recorre à média aritmética simples dos robôs selecionados.
 *
 * @param {Array<string|number>} selectedRobotIds Robôs ativos no filtro.
 * @param {Object} robotProductivityMap Mapa { [robot_id]: human_productivity }.
 * @param {Array<Object>} cleaningTasks Lista de tarefas de limpeza do período.
 * @param {number} defaultProductivity Valor padrão para fallbacks (default: 150).
 * @returns {number} Taxa de produtividade humana efetiva (m²/h).
 */
export function calculateEffectiveFleetHumanProductivity(
    selectedRobotIds,
    robotProductivityMap,
    cleaningTasks,
    defaultProductivity = DEFAULT_CLEANING_APP_SETTINGS.human_productivity
) {
    if (!Array.isArray(selectedRobotIds) || selectedRobotIds.length === 0) {
        return defaultProductivity;
    }

    const productivityMap =
        robotProductivityMap && typeof robotProductivityMap === 'object'
            ? robotProductivityMap
            : {};
    const tasks = Array.isArray(cleaningTasks) ? cleaningTasks : [];

    let totalWeightedProd = 0;
    let totalCleanTime = 0;
    const selectedSet = new Set(selectedRobotIds.map(String));

    tasks.forEach(task => {
        const robotId =
            (task && task._robot && task._robot.id) ||
            (task && task.robot_id) ||
            null;

        if (
            robotId != null &&
            selectedSet.size > 0 &&
            !selectedSet.has(String(robotId))
        ) {
            return;
        }

        const cleanTime = Number(task && task.clean_time) || 0;
        if (cleanTime <= 0) return;

        let robotProdRaw = null;
        if (robotId != null) {
            robotProdRaw =
                productivityMap[robotId] != null
                    ? productivityMap[robotId]
                    : productivityMap[String(robotId)];
        }
        const robotProd =
            robotProdRaw != null && Number.isFinite(Number(robotProdRaw))
                ? Number(robotProdRaw)
                : defaultProductivity;

        totalWeightedProd += cleanTime * robotProd;
        totalCleanTime += cleanTime;
    });

    if (totalCleanTime > 0) {
        return Number((totalWeightedProd / totalCleanTime).toFixed(2));
    }

    // Fallback: se nenhuma tarefa ocorreu no período, calcula a média simples dos robôs selecionados
    let sum = 0;
    let count = 0;
    selectedRobotIds.forEach(id => {
        const prodRaw =
            productivityMap[id] != null
                ? productivityMap[id]
                : productivityMap[String(id)];
        const prod =
            prodRaw != null && Number.isFinite(Number(prodRaw))
                ? Number(prodRaw)
                : defaultProductivity;
        sum += prod;
        count += 1;
    });

    return count > 0 ? Number((sum / count).toFixed(2)) : defaultProductivity;
}
